import React, { useState, useEffect } from 'react';
import { FiShoppingCart, FiCreditCard, FiTrash2, FiClock, FiCheckCircle, FiTruck, FiPlus, FiMinus } from 'react-icons/fi';
import { useWholesaleCart } from '../../contexts/WholesaleCartContext';
import { useWholesaleAuth } from '../../contexts/WholesaleAuthContext';
import { db } from '../../services/firebase';
import { collection, query, getDocs, orderBy, where } from 'firebase/firestore';
import styles from './CartAndOrders.module.css';
import { useNavigate } from 'react-router-dom';

// This component can be expanded later
const placeholderOrders = [
  { id: 'ORD-12345', date: '2023-06-15', status: 'Delivered', total: 277.50, items: 2 },
  { id: 'ORD-12346', date: '2023-06-20', status: 'Shipped', total: 120.00, items: 1 },
  { id: 'ORD-12347', date: '2023-06-22', status: 'Processing', total: 62.00, items: 1 },
];

const StatusBadge = ({ status }) => {
  const statusMap = {
    Processing: { icon: <FiClock />, className: styles.processing },
    Shipped: { icon: <FiTruck />, className: styles.shipped },
    Delivered: { icon: <FiCheckCircle />, className: styles.delivered },
  };
  const currentStatus = statusMap[status] || { icon: <FiClock />, className: '' };
  return (
    <div className={`${styles.statusBadge} ${currentStatus.className}`}>
      {currentStatus.icon}
      <span>{status}</span>
    </div>
  );
};

const WholesaleCartAndOrders = () => {
  const [activeTab, setActiveTab] = useState('cart');
  const { 
    items, 
    loading, 
    updateQuantity, 
    removeItem, 
    clearCart, 
    totalItems,
    subtotal 
  } = useWholesaleCart();
  const { currentWholesaleUser, wholesaleCustomer } = useWholesaleAuth();
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const navigate = useNavigate();
  
  const isDemoAccount = wholesaleCustomer?.status === 'demo';

  const shipping = 30.00;
  const cartTotal = subtotal + shipping;

  useEffect(() => {
    if (activeTab === 'history' && wholesaleCustomer) {
      const fetchOrders = async () => {
        setLoadingOrders(true);
        try {
          // Query the new wholesaleOrders collection for this customer
          const ordersRef = collection(db, 'wholesaleOrders');
          const q = query(
            ordersRef, 
            where('customerId', '==', wholesaleCustomer.id),
            orderBy('createdAt', 'desc')
          );
          const snapshot = await getDocs(q);
          setOrders(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        } catch (error) {
          console.error('Error fetching orders:', error);
        }
        setLoadingOrders(false);
      };
      fetchOrders();
    }
  }, [activeTab, wholesaleCustomer]);

  const CartView = () => {
    if (loading) return <p>Loading cart...</p>;
    
    if (isDemoAccount) {
      return (
        <div className={styles.demoMessage}>
          <div className={styles.demoIcon}>👁️</div>
          <h3>Demo Account - Cart Disabled</h3>
          <p>This is a demo account for viewing products and pricing only.</p>
          <p>To place orders, please request full wholesale access from your account manager.</p>
        </div>
      );
    }
    
    if (items.length === 0) return <p>Your wholesale cart is empty.</p>;

    return (
        <div className={styles.cartLayout}>
            <div className={styles.cartItems}>
                {items.map(item => (
                    <div key={item.id} className={styles.cartItem}>
                        <img src={item.imageUrl || 'https://via.placeholder.com/64'} alt={item.name} className={styles.itemImage} />
                        <div className={styles.itemDetails}>
                            <div className={styles.itemName}>{item.name}</div>
                            <div className={styles.itemPrice}>QAR {Number(item.wholesalePrice).toFixed(2)} / {item.unit}</div>
                        </div>
                        <div className={styles.quantitySelector}>
                            <button onClick={() => updateQuantity(item.id, item.quantity - 1)}><FiMinus size={12} /></button>
                            <span>{item.quantity}</span>
                            <button onClick={() => updateQuantity(item.id, item.quantity + 1)}><FiPlus size={12} /></button>
                        </div>
                        <div className={styles.itemTotal}>QAR {(item.wholesalePrice * item.quantity).toFixed(2)}</div>
                        <button className={styles.removeItemButton} onClick={() => removeItem(item.id)}><FiTrash2 /></button>
                    </div>
                ))}
            </div>
            <div className={styles.cartSummary}>
                <div className={styles.summaryTitle}>Order Summary</div>
                <div className={styles.summaryRow}>
                    <span>Subtotal ({totalItems} items)</span>
                    <span>QAR {subtotal.toFixed(2)}</span>
                </div>
                <div className={styles.summaryRow}>
                    <span>Shipping</span>
                    <span>QAR {shipping.toFixed(2)}</span>
                </div>
                <div className={`${styles.summaryRow} ${styles.summaryTotal}`}>
                    <span>Total</span>
                    <span>QAR {cartTotal.toFixed(2)}</span>
                </div>
                <button className={styles.checkoutButton} onClick={() => navigate('/wholesale-dashboard/checkout')}>
                    Checkout
                </button>
                <button className={styles.clearCartButton} onClick={clearCart}>Clear Cart</button>
            </div>
        </div>
    );
  };

  const OrderHistoryView = () => {
    if (loadingOrders) return <p>Loading order history...</p>;
    if (orders.length === 0) return <p>You have not placed any orders yet.</p>;

    return (
      <div className={styles.orderHistory}>
         <div className={styles.orderTableHeader}>
            <div>Order ID</div>
            <div>Date</div>
            <div>Status</div>
            <div>Items</div>
            <div>Total</div>
         </div>
         {orders.map(order => (
           <div key={order.id} className={styles.orderRow}>
             <div className={styles.orderId}>{order.id.substring(0, 8)}...</div>
             <div>{new Date(order.createdAt.seconds * 1000).toLocaleDateString()}</div>
             <div><StatusBadge status={order.status} /></div>
             <div>{order.itemCount}</div>
             <div>QAR {order.total.toFixed(2)}</div>
           </div>
         ))}
      </div>
    );
  };

  return (
     <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerTitle}>Cart & Orders</div>
        <div className={styles.headerSubtitle}>Manage your current cart and view your order history.</div>
      </div>

      <div className={styles.tabContainer}>
        <button 
          className={`${styles.tabButton} ${activeTab === 'cart' ? styles.activeTab : ''}`}
          onClick={() => setActiveTab('cart')}
        >
          <FiShoppingCart />
          <span>Shopping Cart</span>
          {totalItems > 0 && <span className={styles.itemCount}>{totalItems}</span>}
        </button>
        <button 
          className={`${styles.tabButton} ${activeTab === 'history' ? styles.activeTab : ''}`}
          onClick={() => setActiveTab('history')}
        >
          <FiCreditCard />
          <span>Order History</span>
        </button>
      </div>

      <div className={styles.content}>
        {activeTab === 'cart' ? <CartView /> : <OrderHistoryView />}
      </div>
    </div>
  );
};

export default WholesaleCartAndOrders; 